import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Clock, MapPin, Sparkles } from 'lucide-react';
import SectionHeader from '../components/SectionHeader';
import MenuCard from '../components/MenuCard';
import SuccessModal from '../components/SuccessModal';
import ErrorModal from '../components/ErrorModal';
import { mockCaptureApi } from '../utils/mockCapture';

const captureSchema = z.object({
  fullName: z.string().min(1, 'Full name is required'),
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
  phone: z.string().optional(),
  consent: z.boolean().refine((val) => val === true, {
    message: 'Please confirm you would like to receive your offer',
  }),
});

type CaptureFormData = z.infer<typeof captureSchema>;

const breakfastDishes = [
  {
    image: '/images/food/breakfast-full.webp',
    title: 'Full Breakfast',
    description:
      'Link sausage, square sausage, bacon, black pudding, haggis, egg, beans, tattie scone, tomatoes & mushrooms with toast.',
    price: '£12.95 Sit In / £11.95 Takeaway',
  },
  {
    image: '/images/food/breakfast-promo.webp',
    title: 'Vegetarian Breakfast',
    description:
      'Vegetarian sausage, hash brown, tattie scone, mushrooms, tomatoes, beans & egg, served with toast.',
    price: '£9.95 Sit In / £8.95 Takeaway',
  },
  {
    image: '/images/food/breakfast-menu.webp',
    title: 'Breakfast Roll',
    description: 'Link sausage, square sausage, bacon, haggis & scrambled egg in a fresh roll.',
    price: '£9.45',
  },
];

const BreakfastOfferPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [discountCode, setDiscountCode] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CaptureFormData>({
    resolver: zodResolver(captureSchema),
  });

  const onSubmit = async (data: CaptureFormData) => {
    setIsSubmitting(true);

    try {
      const response = await mockCaptureApi.submit({
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
      });

      if (response.success) {
        setDiscountCode(response.discountCode || '');
        setIsModalOpen(true);
        reset();
      } else {
        setErrorMessage(response.error || 'An error occurred while claiming your offer.');
        setIsErrorModalOpen(true);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setErrorMessage('Unable to submit your details. Please try again later.');
      setIsErrorModalOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-primary-black min-h-screen">
      {/* Hero */}
      <section className="relative pt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/food/breakfast-full.webp"
            alt="Full Scottish breakfast at The Creamery"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-black/80 via-primary-black/85 to-primary-black"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center">
          <p className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-primary-gold mb-6">
            <Sparkles className="w-4 h-4" />
            Now Serving Breakfast
          </p>
          <h1 className="text-4xl md:text-6xl font-bold text-white font-display mb-6">
            Breakfast is Here at{' '}
            <span className="text-primary-gold">The Creamery</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-10">
            A hearty, freshly cooked Scottish breakfast is now on the menu every day.
            Leave your details below and we'll send you <strong className="text-primary-gold">10% off</strong> your
            first breakfast with us.
          </p>
          <a
            href="#claim-offer"
            className="inline-block bg-primary-gold text-primary-black px-8 py-4 rounded-full font-semibold text-lg uppercase tracking-wider hover:bg-primary-black hover:text-primary-gold hover:border-2 hover:border-primary-gold transition-all"
          >
            Claim Your 10% Off
          </a>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-10 text-gray-300">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary-gold" />
              <span>Sun-Fri: 12pm - 7pm &nbsp;|&nbsp; Sat: 12pm - 7:30pm</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary-gold" />
              <span>Bainsford Main Street, Falkirk</span>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Highlights */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="What's on the Menu"
            title="A Proper Scottish Breakfast"
            subtitle="Cooked to order with quality, locally sourced ingredients - dine in or grab it on the go."
            darkMode={true}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {breakfastDishes.map((dish) => (
              <MenuCard key={dish.title} {...dish} />
            ))}
          </div>
        </div>
      </section>

      {/* Capture Form */}
      <section id="claim-offer" className="relative py-20 overflow-hidden scroll-mt-20">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-gold opacity-5 rounded-full blur-3xl"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Limited Time Offer"
            title="Get 10% Off Your Next Breakfast"
            subtitle="Tell us where to send your discount code - it only takes a moment."
            darkMode={true}
          />

          <div className="max-w-xl mx-auto">
            <div className="border border-primary-gold rounded-lg p-8 bg-primary-black bg-opacity-50 backdrop-blur-sm">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-6">
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-400 mb-2 uppercase tracking-wider">
                    Full Name
                  </label>
                  <input
                    {...register('fullName')}
                    type="text"
                    id="fullName"
                    placeholder="John Smith"
                    className={`w-full px-4 py-3 bg-transparent border rounded-md text-white placeholder-gray-600 focus:outline-none focus:border-primary-gold transition-colors ${
                      errors.fullName ? 'border-red-500' : 'border-gray-700'
                    }`}
                  />
                  {errors.fullName && (
                    <p className="text-red-500 text-sm mt-1">{errors.fullName.message}</p>
                  )}
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2 uppercase tracking-wider">
                    Email Address
                  </label>
                  <input
                    {...register('email')}
                    type="email"
                    id="email"
                    placeholder="john@example.com"
                    className={`w-full px-4 py-3 bg-transparent border rounded-md text-white placeholder-gray-600 focus:outline-none focus:border-primary-gold transition-colors ${
                      errors.email ? 'border-red-500' : 'border-gray-700'
                    }`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div className="mb-6">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-400 mb-2 uppercase tracking-wider">
                    Phone Number <span className="normal-case text-gray-600">(optional)</span>
                  </label>
                  <input
                    {...register('phone')}
                    type="tel"
                    id="phone"
                    placeholder="+44 7700 000000"
                    className="w-full px-4 py-3 bg-transparent border border-gray-700 rounded-md text-white placeholder-gray-600 focus:outline-none focus:border-primary-gold transition-colors"
                  />
                </div>

                <div className="mb-8">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      {...register('consent')}
                      type="checkbox"
                      className="mt-1 w-4 h-4 accent-primary-gold"
                    />
                    <span className="text-sm text-gray-400">
                      Yes, send me my 10% off code and keep me updated on offers from The Creamery.
                    </span>
                  </label>
                  {errors.consent && (
                    <p className="text-red-500 text-sm mt-2">{errors.consent.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary-gold text-primary-black py-4 rounded-md font-semibold text-lg uppercase tracking-wider hover:bg-primary-black hover:text-primary-gold hover:border-2 hover:border-primary-gold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Send Me My Discount'}
                </button>

                <p className="text-xs text-gray-600 text-center mt-4">
                  We'll only use your details to send your offer. See our{' '}
                  <a href="/privacy" className="text-primary-gold hover:underline">
                    Privacy Policy
                  </a>{' '}
                  for details.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <SuccessModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Your Discount is On Its Way!"
        message={`Thanks for signing up! Use code ${discountCode} to get 10% off your next breakfast at The Creamery.`}
      />

      <ErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        title="Something Went Wrong"
        message={errorMessage}
      />
    </div>
  );
};

export default BreakfastOfferPage;
